<template>
    <div
        :id="id"
        :class="classString"
    >
        <lni-radios
            :id="`${id}_preliminary`"
            :labelText="text.preliminary"
            :options="preliminaryOptions"
            :isColumn="false"
            :required="true"
            :changeAction="`${id}/syncStore`"
        />
        <div
            role="status"
            class="lni-u-mb3"
        >
            <transition name="lni-o-slide">
                <lni-informational-message
                    v-if="!hasMinorsInvolved"
                    :id="`${id}_disclaimer`"
                    class="lni-u-pv2 lni-u-ph2 lni-u-mt2"
                >
                    <lni-html-element
                        :id="`${id}_disclaimer--message`"
                        :htmlString="text.disclaimer"
                    />
                </lni-informational-message>
            </transition>
        </div>
        <lni-checkbox-list
            :id="`${id}_violations`"
            class="lni-u-mb2"
            :required="true"
            :labelText="text.complaintLabel"
            :options="violationOptions"
            :changeAction="`${id}/syncStore`"
        />
        <lni-input-textarea
            :id="`${id}_description`"
            class="lni-u-full-width lni-u-mb3"
            :labelText="text.descriptionLabel"
            :required="true"
            :limit="4000"
            :changeAction="`${id}/syncStore`"
        />
        <fieldset class="lni-u-full-width lni-u-flex lni-u-flex-wrap lni-u-mv1">
            <legend class="lni-u-heading--4">
                {{ text.dateRange }}
            </legend>
            <lni-input-date
                :id="`${id}_complaint-start`"
                class="lni-u-pr3 lni-u-mb2"
                :labelText="text.startDate"
                :customValidityAction="`${id}/complaintDurationCustomValidation`"
                :changeAction="[`${id}/checkComplaintDuration`, `${id}/syncStore`]"
            />
            <lni-input-date
                :id="`${id}_complaint-end`"
                class="lni-u-mb2"
                :labelText="text.endDate"
                :changeAction="[`${id}/checkComplaintDuration`, `${id}/syncStore`]"
            />
        </fieldset>
        <div class="lni-u-form-width--base lni-u-mb2">
            <lni-select
                :id="`${id}_complainant`"
                class="lni-u-full-width"
                :labelText="text.complainantLabel"
                :options="[
                    {
                        text: text.me,
                        value: text.me
                    },
                    {
                        text: text.someoneElse,
                        value: text.someoneElse,
                    },
                    {
                        text: text.multipleMinors,
                        value: text.multipleMinors,
                    },
                ]"
                :required="true"
                @change="onComplainantChange"
            />
        </div>
        <lni-collapse
            :id="`${id}_quantity-collapse`"
            class="lni-u-flex lni-u-mb2"
        >
            <span
                :id="`${id}_quantity-label`"
                class="lni-u-mvauto lni-u-pr2"
            >
                {{ text.quantityLabel }}
            </span>
            <lni-input-text
                :id="`${id}_quantity`"
                type="number"
                customWidth="5"
                :aria-labelledby="`${id}_quantity-label`"
                min="1"
                :required="true"
                :changeAction="`${id}/syncStore`"
            />
        </lni-collapse>
        <div class="lni-u-form-width--base">
            <lni-select
                :id="`${id}_relationship`"
                class="lni-u-full-width"
                :labelText="text.relationshipLabel"
                :options="[
                    {
                        text: text.self,
                        value: text.self,
                    },
                    {
                        text: text.parent,
                        value: text.parent,
                    },
                    {
                        text: text.other,
                        value: text.other,
                    },
                ]"
                :changeAction="`${id}/syncStore`"
                :required="true"
                @change="toggleAdditionalQuestions"
            >
                <template #helperText>
                    <transition name="lni-o-slide">
                        <div v-if="forSelf">
                            {{ text.selfMessage }}
                        </div>
                    </transition>
                </template>
            </lni-select>
        </div>
        <lni-collapse
            :id="`${id}_additional-collapse`"
        >
            <div class="lni-u-full-width lni-u-flex lni-u-flex-wrap">
                <lni-input-text
                    :id="`${id}_first-name`"
                    class="lni-u-mb2 lni-u-mr1"
                    :labelText="text.minorFirstName"
                    :changeAction="`${id}/syncStore`"
                />
                <lni-input-text
                    :id="`${id}_last-name`"
                    class="lni-u-mb2"
                    :labelText="text.minorLastName"
                    :changeAction="`${id}/syncStore`"
                />
            </div>
            <lni-input-date
                :id="`${id}_birth-date`"
                class="lni-u-mb2"
                :labelText="text.birthDate"
                :changeAction="`${id}/syncStore`"
            />
            <lni-input-date
                :id="`${id}_work-start`"
                class="lni-u-pr3 lni-u-mb2"
                :labelText="text.workStart"
                :changeAction="`${id}/syncStore`"
            />
            <lni-input-date
                :id="`${id}_work-end`"
                class="lni-u-mb2"
                :labelText="text.workEnd"
                :helperText="text.currentlyEmployed"
                :changeAction="`${id}/syncStore`"
            />
            <div class="lni-u-form-width--base">
                <lni-select
                    :id="`${id}_end-reason`"
                    class="lni-u-full-width lni-u-mb2"
                    :labelText="text.terminationReason"
                    :options="terminationOptions"
                    :changeAction="`${id}/syncStore`"
                    @change="toggleField(`${id}_reason-collapse`, `${id}_other-reason`, hasOtherReason)"
                />
                <lni-collapse
                    :id="`${id}_reason-collapse`"
                    class="lni-u-form-width--base"
                >
                    <lni-input-text
                        :id="`${id}_other-reason`"
                        class="lni-u-mb2"
                        :labelText="text.otherReason"
                        maxlength="50"
                        :required="true"
                        :fullWidth="true"
                        :changeAction="`${id}/syncStore`"
                    />
                </lni-collapse>
            </div>
        </lni-collapse>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
    name : 'LniChildLaborComplaintInformation',
    props: {
        classString: {
            type   : String,
            default: '',
        },
        text: {
            type    : Object,
            required: true,
        },
    },
    emits   : ['update-add', 'update-remove'],
    computed: {
        forSomeoneElse() {
            const value = this.$store.state[`${ this.id }_complainant`]?.value;
            return value ? value === this.text.someoneElse : false;
        },
        hasMinorsInvolved() {
            const value = this.$store.state[`${ this.id }_preliminary`]?.value;
            const yesOption = this.$store.getters['appState/selectedOptionByText']('preliminaryOptions', this.text.yes);
            return value ? value === yesOption.value : true;
        },
        hasOtherReason() {
            const value = this.$store.state[`${ this.id }_end-reason`]?.value;
            const otherOption = this.$store.getters['appState/selectedOptionByText']('terminationOptions', this.text.other);
            return value ? value === otherOption.value : false;
        },
        isFamiliar() {
            const relationship = this.$store.state[`${ this.id }_relationship`]?.value;
            const forSelf = relationship === this.text.self;
            const isParent = relationship === this.text.parent;

            let result = false;
            if (forSelf) {
                result = true;
            } else {
                if (isParent && !this.forMultipleMinors) {
                    result = true;
                }
            }
            return result;
        },
        ...mapGetters({
            forMultipleMinors : 'appState/forMultipleMinors',
            forSelf           : 'appState/forSelf',
            preliminaryOptions: 'appState/preliminaryOptions',
            terminationOptions: 'appState/terminationOptions',
            violationOptions  : 'appState/violationOptions',
        }),
    },
    mounted() {
        // Add custom validity test for invalid complaint duration on start date
        this.$store.commit(`${ this.id }_complaint-start/addValidityTest`, {
            validityTest: 'invalidDuration',
            global      : () => this.text.durationErrorGlobal,
            inline      : () => this.text.durationErrorInline,
        }, { root: true });

        // Add custom validation message for quantity because label is blank
        this.$store.commit(`${ this.id }_quantity/customizeValidationMessage`, {
            validationTest: 'valueMissing',
            global        : () => `"${ this.text.quantityLabel }" is required.`,
            inline        : () => `This field is required.`,
        }, { root: true });

        this.$store.commit(`${ this.id }/ADD_TRACKED_ERRORS`, [
            `${ this.id }_preliminary`,
            `${ this.id }_violations`,
            `${ this.id }_description`,
            `${ this.id }_complaint-start`,
            `${ this.id }_complaint-end`,
            `${ this.id }_complainant`,
            `${ this.id }_relationship`,
            `${ this.id }_first-name`,
            `${ this.id }_last-name`,
            `${ this.id }_work-start`,
            `${ this.id }_work-end`,
            `${ this.id }_end-reason`,
        ]);

        this.$emit('update-add', this.trackedErrors);
    },
    methods: {
        async onComplainantChange() {
            await this.$store.dispatch(`${ this.id }/syncStore`);

            const targetId = `${ this.id }_relationship`;
            if (this.forSelf) {
                this.setSelf(targetId);
            } else {
                this.unsetSelf(targetId);

                if (this.forSomeoneElse) {
                    this.setSomeoneElse(targetId);
                } else {
                    this.unsetSomeoneElse(targetId);
                }
            }

            this.toggleField(`${ this.id }_quantity-collapse`, `${ this.id }_quantity`, this.forMultipleMinors);
            this.toggleAdditionalQuestions();
        },
        setSelf(targetId) {
            const options = [];
            this.$store.state[targetId].options.forEach(opt => {
                options.push({
                    ...opt,
                    disabled: opt.value !== this.text.self,
                });
            });
            this.$store.commit('setAttribute', {
                id       : targetId,
                attribute: 'options',
                value    : options,
            }, { root: true });

            this.$store.commit('setAttribute', {
                id       : targetId,
                attribute: 'value',
                value    : this.text.self,
            }, { root: true });
        },
        setSomeoneElse(targetId) {
            const targetState = this.$store.state[targetId];

            const options = [];
            targetState.options.forEach(opt => {
                options.push({
                    ...opt,
                    disabled: opt.value === this.text.self,
                });
            });
            this.$store.commit('setAttribute', {
                id       : targetId,
                attribute: 'options',
                value    : options,
            }, { root: true });

            if (targetState.value === this.text.self) {
                this.$store.commit('setAttribute', {
                    id       : targetId,
                    attribute: 'value',
                    value    : '',
                }, { root: true });
            }
        },
        async toggleAdditionalQuestions() {
            this.$store.commit('setAttribute', {
                id       : `${ this.id }_additional-collapse`,
                attribute: 'visible',
                value    : this.isFamiliar,
            }, { root: true });

            // Clear fields within lni-collapse if collapsed/hidden
            if (!this.isFamiliar) {
                const ids = [
                    `${ this.id }_first-name`,
                    `${ this.id }_last-name`,
                    `${ this.id }_birth-date`,
                    `${ this.id }_work-start`,
                    `${ this.id }_work-end`,
                    `${ this.id }_end-reason`,
                    `${ this.id }_other-reason`,
                ];
                ids.forEach(id => {
                    this.$store.commit('setAttribute', {
                        id,
                        attribute: 'value',
                        value    : '',
                    }, { root: true });
                });
            }

            await this.$store.dispatch(`${ this.id }/syncStore`);
        },
        /**
         * Toggles the visibility of a field and its `<lni-collapse>` wrapper, and clears the value of the field if hidden.
         * @param collapseId The `id` of the `<lni-collapse>` wrapper
         * @param targetId The `id` of the field
         * @param visible Whether the field is visible or not
         */
        async toggleField(collapseId, targetId, visible) {
            this.$store.commit('setAttribute', {
                id       : collapseId,
                attribute: 'visible',
                value    : visible,
            }, { root: true });

            if (visible) {
                const targetState = this.$store.state[targetId];
                if (targetState.required) {
                    this.$store.commit(`${ this.id }/ADD_TRACKED_ERRORS`, [targetId]);
                    this.$emit('update-add', [targetId]);
                }
            } else {
                this.$store.commit('setAttribute', {
                    id       : targetId,
                    attribute: 'value',
                    value    : '',
                }, { root: true });

                this.$store.commit(`${ this.id }/REMOVE_TRACKED_ERRORS`, [targetId]);
                this.$emit('update-remove', [targetId]);
                await this.$store.dispatch(`${ this.id }/syncStore`);
            }
        },
        unsetSelf(targetId) {
            const targetState = this.$store.state[targetId];
            const options = [];
            targetState.options.forEach(opt => {
                options.push({
                    ...opt,
                    disabled: false,
                });
            });
            this.$store.commit('setAttribute', {
                id       : targetId,
                attribute: 'options',
                value    : options,
            }, { root: true });

            if (targetState.value === this.text.self && !this.forMultipleMinors) {
                this.$store.commit('setAttribute', {
                    id       : targetId,
                    attribute: 'value',
                    value    : '',
                }, { root: true });
            }
        },
        unsetSomeoneElse(targetId) {
            const options = [];
            this.$store.state[targetId].options.forEach(opt => {
                options.push({
                    ...opt,
                    disabled: false,
                });
            });
            this.$store.commit('setAttribute', {
                id       : targetId,
                attribute: 'options',
                value    : options,
            }, { root: true });
        },
    },
};
</script>